// ScrollIntoViewDirective: Uses Angular signals and effect for scroll/focus behavior.
import { Directive, ElementRef, input, inject, effect, InputSignal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { WINDOW_TOKEN } from '../../services/window.token';

@Directive({
  selector: '[tdWbScrollIntoView]',
  exportAs: 'tdWbScrollIntoView'
})
export class ScrollIntoViewDirective {
  // Inputs as signals for reactive scroll/focus control
  // Observable input triggers scroll/focus actions
  tdWbScrollIntoView: InputSignal<Observable<void> | undefined> = input<Observable<void> | undefined>(undefined);
  // If true, scrolls to top of window instead of element
  scrollTop: InputSignal<boolean> = input<boolean>(false);
  // If true, focuses the element after scrolling
  elementOnFocus: InputSignal<boolean> = input<boolean>(false);

  private subscription: Subscription | undefined;
  private readonly elementRef = inject(ElementRef);
  private readonly window = inject(WINDOW_TOKEN);

  constructor() {
    // effect: subscribes to the scroll$ Observable and calls window.scrollTo() /
    // element.scrollIntoView() — both are imperative browser APIs; onCleanup
    // unsubscribes when the Observable input changes between re-runs.
    effect((onCleanup: (cleanupFn: () => void) => void) => {

      const scroll$ = this.tdWbScrollIntoView();
      if (scroll$) {
        // Subscribe to scroll$ and handle scroll/focus logic
        this.subscription = scroll$.subscribe(() => {
          if (this.scrollTop()) {
            this.window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Optionally focus element after scrolling, avoid focusing element inside aria-hidden subtree
            if (this.elementOnFocus() && !this.elementRef.nativeElement.closest?.('[aria-hidden="true"]')) {
              this.elementRef.nativeElement.focus();
            }
          }
        });
      }
      // Ensure subscription is cleaned up when effect is destroyed
      onCleanup(() => {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
      });
    });
  }
}
